import { GEOMETRY_EPSILON } from '@/constants/numeric';

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export const v2 = (x: number, y: number): Vec2 => ({ x, y });
export const add2 = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
export const sub2 = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
export const scale2 = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });
export const dot2 = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
/** 2D scalar cross product (z component of the 3D cross). */
export const cross2 = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;
export const len2 = (a: Vec2): number => Math.hypot(a.x, a.y);
export const lenSq2 = (a: Vec2): number => a.x * a.x + a.y * a.y;
export const dist2 = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
export const distSq2 = (a: Vec2, b: Vec2): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};
export const lerp2 = (a: Vec2, b: Vec2, t: number): Vec2 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});
export const mid2 = (a: Vec2, b: Vec2): Vec2 => lerp2(a, b, 0.5);

export function normalize2(a: Vec2): Vec2 {
  const l = len2(a);
  if (l < GEOMETRY_EPSILON) return { x: 0, y: 0 };
  return { x: a.x / l, y: a.y / l };
}

/** Rotate 90 degrees counter-clockwise. */
export const perp2 = (a: Vec2): Vec2 => ({ x: -a.y, y: a.x });

export function rotate2(a: Vec2, angle: number): Vec2 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
}

/** Signed angle from a to b in (-pi, pi]. */
export function angleBetween2(a: Vec2, b: Vec2): number {
  return Math.atan2(cross2(a, b), dot2(a, b));
}

/** Polar angle of a in (-pi, pi]. */
export const heading2 = (a: Vec2): number => Math.atan2(a.y, a.x);

export function equals2(a: Vec2, b: Vec2, tol = GEOMETRY_EPSILON): boolean {
  return Math.abs(a.x - b.x) <= tol && Math.abs(a.y - b.y) <= tol;
}
